import React from "react";
import { Box, Paper, Typography } from "@mui/material";

const BRAND_PRIMARY = "#9D2235";
const BRAND_BG = "#fdf2f4";

// Known section headings returned by the AI prompt
const SECTION_HEADINGS = [
  "Why the Correct Answer is Right:",
  "Common Mistake:",
  "Tip for Similar Questions:",
];

/**
 * Parses structured explanation text and renders each section with a heading.
 * Falls back to rendering the raw text if no headings are found.
 */
function parseExplanation(text) {
  const sections = [];
  let remaining = text.trim();

  for (let i = 0; i < SECTION_HEADINGS.length; i++) {
    const heading = SECTION_HEADINGS[i];
    const idx = remaining.indexOf(heading);
    if (idx === -1) continue;

    // Find where the next heading starts (if any)
    let bodyStart = idx + heading.length;
    let bodyEnd = remaining.length;

    for (let j = i + 1; j < SECTION_HEADINGS.length; j++) {
      const nextIdx = remaining.indexOf(SECTION_HEADINGS[j], bodyStart);
      if (nextIdx !== -1 && nextIdx < bodyEnd) {
        bodyEnd = nextIdx;
      }
    }

    const body = remaining.slice(bodyStart, bodyEnd).trim();
    if (body && body.toLowerCase() !== "n/a") {
      sections.push({ heading, body });
    }
  }

  return sections;
}

export default function ExplanationBlock({ text }) {
  if (!text) return null;

  const sections = parseExplanation(text);

  return (
    <Paper
      sx={{
        mt: 2,
        p: 2,
        bgcolor: BRAND_BG,
        borderLeft: `4px solid ${BRAND_PRIMARY}`,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 700,
          color: BRAND_PRIMARY,
          mb: 1,
          fontSize: "0.75rem",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        }}
      >
        Explanation
      </Typography>

      {sections.length > 0 ? (
        sections.map(({ heading, body }) => (
          <Box key={heading} sx={{ mb: 1.5 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: BRAND_PRIMARY,
                fontSize: "0.78rem",
                mb: 0.3,
              }}
            >
              {heading.replace(/:$/, "")}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#333", lineHeight: 1.6 }}
            >
              {body}
            </Typography>
          </Box>
        ))
      ) : (
        // Fallback: render as plain text if structure not detected
        <Typography variant="body2" sx={{ color: "#333", lineHeight: 1.6 }}>
          {text}
        </Typography>
      )}
    </Paper>
  );
}
